// src/pages/admin/utils/limitsUtils.js

export const DEFAULT_LIMITS = {
  maxSections: null,           // null = unlimited
  maxImagesPerSection: null,   // null = unlimited
  maxNavLinks: null,           // null = unlimited
  maxFooterLinks: null,        // null = unlimited
};

function toIntOrNull(v) {
  if (v === "" || v === null || typeof v === "undefined") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  if (n < 0) return null;
  return Math.floor(n);
}

export function normalizeLimits(raw = {}) {
  return {
    ...DEFAULT_LIMITS,
    maxSections: toIntOrNull(raw.maxSections),
    maxImagesPerSection: toIntOrNull(raw.maxImagesPerSection),
    maxNavLinks: toIntOrNull(raw.maxNavLinks),
    maxFooterLinks: toIntOrNull(raw.maxFooterLinks),
  };
}

function isUrlString(s) {
  if (typeof s !== "string") return false;
  const t = s.trim().toLowerCase();
  if (!t) return false;
  return (
    t.startsWith("http://") ||
    t.startsWith("https://") ||
    t.startsWith("/uploads/") ||
    t.startsWith("data:image/")
  );
}

function keyLooksImagey(key) {
  const k = String(key || "");
  return /image|images|logo|banner|gallery|carousel|photo|cover|avatar/i.test(k);
}

function countImageLikeStrings(value, parentKey = "") {
  if (typeof value === "string") {
    return isUrlString(value) && keyLooksImagey(parentKey) ? 1 : 0;
  }
  if (Array.isArray(value)) {
    return value.reduce((acc, v) => acc + countImageLikeStrings(v, parentKey), 0);
  }
  if (value && typeof value === "object") {
    let sum = 0;
    for (const [k, v] of Object.entries(value)) {
      // We pass the current key so nested things like config.images, item.image, etc get counted.
      sum += countImageLikeStrings(v, k);
    }
    return sum;
  }
  return 0;
}

function countImagesInSection(section) {
  // Heuristic: counts url-like strings in keys that look like image fields.
  // Works across different template shapes without needing per-template rules.
  return (
    countImageLikeStrings(section?.config || {}, "config") +
    countImageLikeStrings(section?.style || {}, "style")
  );
}

function footerLinksCount(footer) {
  const cfg = footer?.config || {};
  const candidates = [
    cfg.links,
    cfg.quickLinks,
    cfg.socialLinks,
    cfg.menuLinks,
  ].filter(Boolean);

  let total = 0;
  for (const v of candidates) {
    if (Array.isArray(v)) total += v.length;
  }
  return total;
}

export function validatePageAgainstLimitsClient({ sections, nav, footer }, rawLimits) {
  const limits = normalizeLimits(rawLimits);
  const errors = [];

  const secCount = Array.isArray(sections) ? sections.length : 0;
  if (limits.maxSections !== null && secCount > limits.maxSections) {
    errors.push(`Too many sections: ${secCount}. Limit is ${limits.maxSections}.`);
  }

  if (limits.maxImagesPerSection !== null && Array.isArray(sections)) {
    sections.forEach((s, idx) => {
      const imgCount = countImagesInSection(s);
      if (imgCount > limits.maxImagesPerSection) {
        errors.push(
          `Section ${idx + 1} (${s?.type || "unknown"}) has ${imgCount} images. Limit is ${limits.maxImagesPerSection}.`
        );
      }
    });
  }

  const navLinks = Array.isArray(nav?.config?.links) ? nav.config.links.length : 0;
  if (limits.maxNavLinks !== null && navLinks > limits.maxNavLinks) {
    errors.push(`Too many nav links: ${navLinks}. Limit is ${limits.maxNavLinks}.`);
  }

  const fLinks = footerLinksCount(footer);
  if (limits.maxFooterLinks !== null && fLinks > limits.maxFooterLinks) {
    errors.push(`Too many footer links: ${fLinks}. Limit is ${limits.maxFooterLinks}.`);
  }

  return { ok: errors.length === 0, errors, limits };
}